import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AddFormComponent,
  FormField,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import {
  SelectionDialogComponent,
  SelectionItem,
  SelectionDialogConfig,
} from '@app/feature/center-business/stock/synchronization/components/shared/selection-dialog/selection-dialog.component';
import { VirtualWarehouseService } from '../services/virtual-warehouse.service';
import {
  VietnamAddressService,
  VietnamProvince,
} from '../../services/vietnam-address.service';
import {
  VirtualWarehouse,
  VirtualWarehouseCreateRequest,
  VirtualWarehouseUpdateRequest,
} from '../models/virtual-warehouse.model';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { PermissionService } from '@app/core/services/permission.service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-virtual-warehouse-form',
  standalone: true,
  imports: [
    CommonModule,
    AddFormComponent,
    ToastModule,
    TranslateModule,
    SelectionDialogComponent,
    ButtonModule,
  ],
  providers: [MessageService],
  templateUrl: './virtual-warehouse-form.component.html',
  styleUrl: './virtual-warehouse-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualWarehouseFormComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private virtualWarehouseService = inject(VirtualWarehouseService);
  private vietnamAddressService = inject(VietnamAddressService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'virtual_warehouse' for warehouse permissions
  wms_approve = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.approve');
  });

  // Data signals
  loading = signal(false);
  submitting = signal(false);
  warehouseData = signal<VirtualWarehouse | null>(null);

  // Form configuration
  formConfig = signal({
    title: '',
    formFields: [] as FormField[],
    headerActions: [] as any[],
    sectionButtons: [] as any[],
    tableSections: [] as TableSection[],
  });

  // Address data
  provinces = signal<VietnamProvince[]>([]);
  selectedCityCodes = signal<string[]>([]);

  // Physical warehouse options (edit mode: by city; add mode: by physical type)
  physicalWarehouseOptions = signal<Array<{ label: string; value: number }>>(
    [],
  );
  loadingPhysicalWarehouses = signal(false);
  selectedWarehouseData = signal<VirtualWarehouse | null>(null);
  candidateWarehouses = signal<
    Array<{
      id: number;
      code?: string;
      name?: string;
      typeName?: string;
      typeCode?: string;
      contactName?: string;
      contactPhone?: string;
      address?: string;
    }>
  >([]);
  selectedWarehouses = signal<
    Array<{
      id: number;
      code?: string;
      name?: string;
      typeName?: string;
      typeCode?: string;
      contactName?: string;
      contactPhone?: string;
      address?: string;
    }>
  >([]);
  tableWarehouseFilter = signal<{ code?: string; name?: string }>({});

  // Dialog state for selecting warehouses
  showWarehouseDialog = signal(false);
  dialogWarehouses = signal<SelectionItem[]>([]);
  dialogLoading = signal(false);
  dialogTotalRecords = signal(0);
  dialogCurrentPage = signal(0);
  dialogRows = signal(20);
  dialogInitialSelection = signal<SelectionItem[]>([]);
  dialogBaseWarehouses = signal<SelectionItem[]>([]);
  dialogConfig = signal<SelectionDialogConfig>(this.buildDialogConfig());

  // Form component reference
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Track previous values
  private lastCityCode: string | null = null;
  private branchId: number | null = null;
  private branchDetailCache: VirtualWarehouse | null = null;
  private loadedBranchMembers = false;
  private skipCanDeactivate = false;

  // Computed values
  isEditMode = computed(() => {
    const url = this.router.url;
    return url.includes('/edit/');
  });
  isAddMode = computed(() => !this.isEditMode());

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.branchId = id ? Number(id) : null;

    this.initializeFormConfig();

    // Load provinces first, then load warehouse data if in edit mode
    this.loadProvincesData().then(async () => {
      if (this.isEditMode() && this.branchId) {
        const detail = await this.loadWarehouseData(this.branchId);
        this.branchDetailCache = detail;
      }
    });

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogConfig.set(this.buildDialogConfig());
        this.updateFormFields();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormConfig(): void {
    const title = this.isEditMode()
      ? this.translateService.instant('virtual_warehouse.form.edit.title')
      : this.translateService.instant('virtual_warehouse.form.add.title');

    const formFields = this.getFormFields();
    const headerActions = this.getHeaderActions();

    this.formConfig.set({
      title,
      formFields,
      headerActions,
      sectionButtons: [],
      tableSections: this.getTableSections(),
    });
  }

  private loadProvincesData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.vietnamAddressService
        .loadProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            this.provinces.set(provinces);
            this.updateFormFields();
            resolve();
          },
          error: (error) => {
            console.error('Error loading provinces:', error);
            reject(error);
          },
        });
    });
  }

  private updateFormFields(): void {
    const currentConfig = this.formConfig();
    const formFields = this.getFormFields();

    this.formConfig.set({
      ...currentConfig,
      formFields,
      tableSections: this.getTableSections(),
    });
  }

  private getFormFields(): FormField[] {
    const basicSection = this.translateService.instant(
      'virtual_warehouse.form.sections.basic_information',
    );
    return [
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.fields.name',
        ),
        controlName: 'name',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'virtual_warehouse.form.placeholders.enter_name',
        ),
        maxLength: 255,
        section: basicSection,
      },
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.fields.city_name',
        ),
        controlName: 'cityCode',
        type: 'searchableDropdown',
        multiple: true,
        required: true,
        placeholder: this.translateService.instant(
          'virtual_warehouse.form.placeholders.select_city',
        ),
        options: this.provinces().map((p) => ({
          label:
            this.translateService.currentLang === 'vi'
              ? p.full_name
              : p.full_name_en,
          value: p.id.toString(),
        })),
        section: basicSection,
      },
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.fields.member_warehouses',
        ),
        controlName: 'memberWarehouseIds',
        type: 'dropdown',
        required: false,
        hidden: true,
        options: this.physicalWarehouseOptions(),
        multiple: true,
        section: basicSection,
      },
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.fields.status',
        ),
        controlName: 'status',
        type: 'dropdown',
        required: true,
        disabled: !this.wms_approve(),
        placeholder: this.translateService.instant(
          'virtual_warehouse.form.placeholders.select_status',
        ),
        defaultValue: true,
        options: [
          {
            label: this.translateService.instant(
              'virtual_warehouse.status.active',
            ),
            value: true,
          },
          {
            label: this.translateService.instant(
              'virtual_warehouse.status.inactive',
            ),
            value: false,
          },
        ],
        section: basicSection,
      },
    ];
  }

  private getHeaderActions(): any[] {
    return [
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.actions.cancel',
        ),
        severity: 'secondary',
        outlined: true,
        action: 'cancel',
      },
      {
        label: this.translateService.instant(
          'virtual_warehouse.form.actions.save',
        ),
        severity: 'info',
        action: 'save',
      },
    ];
  }

  private getTableSections(): TableSection[] {
    const selection = this.selectedWarehouses();
    const cityCodes = this.selectedCityCodes();
    const data = cityCodes.length > 0 ? this.getFilteredWarehousesData() : [];
    const hasSelection = selection.length > 0;
    const hasCity = cityCodes.length > 0;

    return [
      {
        title: this.translateService.instant(
          'virtual_warehouse.form.sections.member_warehouses',
        ),
        show: true,
        data,
        columns: this.getMemberWarehouseTableColumns(),
        rows: 20,
        first: 0,
        totalRecords: data.length,
        loading:
          cityCodes.length > 0 ? this.loadingPhysicalWarehouses() : false,
        hasCheckbox: true,
        hasSerialColumn: true,
        selection,
        actions: [
          {
            label: this.translateService.instant(
              'virtual_warehouse.actions.select_warehouses',
            ),
            action: 'selectWarehouses',
            severity: 'info',
            outlined: false,
            disabled: !hasCity || this.loadingPhysicalWarehouses(),
            icon: '',
          },
          {
            label: this.translateService.instant(
              'virtual_warehouse.actions.delete',
            ),
            action: 'deleteSelected',
            severity: 'danger',
            outlined: true,
            disabled: !hasSelection,
            icon: 'delete',
          },
        ],
      },
    ];
  }

  getMemberWarehouseTableColumns(): TableColumn[] {
    return [
      {
        prop: 'code',
        label: this.translateService.instant(
          'virtual_warehouse.warehouse.table.warehouse_code',
        ),
        type: 'text',
        width: 160,
      },
      {
        prop: 'name',
        label: this.translateService.instant(
          'virtual_warehouse.warehouse.table.warehouse_name',
        ),
        type: 'text',
        width: 240,
      },
      {
        prop: 'typeName',
        label: this.translateService.instant(
          'virtual_warehouse.warehouse.table.warehouse_type',
        ),
        type: 'text',
        template: 'warehouseType',
        width: 180,
      },
      {
        prop: 'actions',
        label: this.translateService.instant(
          'virtual_warehouse.table.columns.actions',
        ),
        type: 'actions',
        width: 200,
        align: 'center',
      },
    ];
  }

  getFilteredWarehousesData(): SelectionItem[] {
    const filter = this.tableWarehouseFilter();
    return this.selectedWarehouses().filter((w) => {
      const code = (w.code || '').toLowerCase();
      const name = (w.name || '').toLowerCase();
      const matchCode = filter.code
        ? code.includes(filter.code.toLowerCase())
        : true;
      const matchName = filter.name
        ? name.includes(filter.name.toLowerCase())
        : true;
      return matchCode && matchName;
    }) as SelectionItem[];
  }

  getMemberWarehouseSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'code',
        label: this.translateService.instant(
          'virtual_warehouse.warehouse.search.warehouse_code',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'virtual_warehouse.warehouse.search.warehouse_code_placeholder',
        ),
      },
      {
        key: 'name',
        label: this.translateService.instant(
          'virtual_warehouse.warehouse.search.warehouse_name',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'virtual_warehouse.warehouse.search.warehouse_name_placeholder',
        ),
      },
    ];
  }

  onTableWarehouseFilter(filter: { code?: string; name?: string }): void {
    this.tableWarehouseFilter.set({
      code: filter.code?.trim() || undefined,
      name: filter.name?.trim() || undefined,
    });
    this.cdr.markForCheck();
  }

  onTableWarehouseFilterReset(): void {
    this.tableWarehouseFilter.set({});
    this.cdr.markForCheck();
  }

  onWarehouseSelectionChange(selection: any[]): void {
    this.selectedWarehouses.set(selection);
    const ids = selection.map((s) => s.id);
    this.setMemberWarehouseIds(ids);
  }

  private buildDialogConfig(): SelectionDialogConfig {
    return {
      // Use 'product' type to allow multi-select in shared dialog without modifying it
      type: 'product',
      title: this.translateService.instant(
        'virtual_warehouse.warehouse.dialog.title',
      ),
      searchFields: [
        {
          key: 'code',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_code',
          ),
          placeholder: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_code_placeholder',
          ),
        },
        {
          key: 'name',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_name',
          ),
          placeholder: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_name_placeholder',
          ),
        },
        {
          key: 'typeCode',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_type',
          ),
          placeholder: this.translateService.instant(
            'virtual_warehouse.warehouse.search.warehouse_type_placeholder',
          ),
        },
      ],
      tableColumns: [
        {
          prop: 'code',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.table.warehouse_code',
          ),
          type: 'text',
        },
        {
          prop: 'name',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.table.warehouse_name',
          ),
          type: 'text',
        },
        {
          prop: 'typeName',
          label: this.translateService.instant(
            'virtual_warehouse.warehouse.table.warehouse_type',
          ),
          type: 'text',
          template: 'warehouseType',
        },
      ],
      width: '900px',
      maxWidth: '90vw',
      confirmButtonLabel: this.translateService.instant(
        'virtual_warehouse.warehouse.dialog.add_button',
      ),
    };
  }

  private mapToSelection(
    items: Array<{
      id: number;
      code?: string;
      name?: string;
      typeName?: string;
      typeCode?: string;
      contactName?: string;
      contactPhone?: string;
      address?: string;
    }>,
  ): SelectionItem[] {
    return items.map((w) => ({
      id: w.id,
      code: w.code || '',
      name: w.name || '',
      typeName: this.translateWarehouseType(w.typeCode, w.typeName),
      typeCode: w.typeCode || '',
      contactName: w.contactName || '',
      contactPhone: w.contactPhone || '',
      address: w.address || '',
    }));
  }

  getWarehouseTypeLabel(row: any): string {
    return this.translateWarehouseType(row?.typeCode, row?.typeName);
  }

  private translateWarehouseType(
    typeCode?: string,
    fallbackName?: string,
  ): string {
    const map: Record<string, string> = {
      main: 'virtual_warehouse.warehouse.types.main',
      main_warehouse: 'virtual_warehouse.warehouse.types.main',
      default: 'virtual_warehouse.warehouse.types.default',
      default_virtual: 'virtual_warehouse.warehouse.types.default_virtual',
      store: 'virtual_warehouse.warehouse.types.store',
      store_cod: 'virtual_warehouse.warehouse.types.store_cod',
      cod_store: 'virtual_warehouse.warehouse.types.store_cod',
      branch: 'virtual_warehouse.warehouse.types.branch',
      branch_warehouse: 'virtual_warehouse.warehouse.types.branch',
      ratio: 'virtual_warehouse.warehouse.types.ratio_virtual',
      ratio_virtual: 'virtual_warehouse.warehouse.types.ratio_virtual',
      qty: 'virtual_warehouse.warehouse.types.qty_virtual',
      qty_virtual: 'virtual_warehouse.warehouse.types.qty_virtual',
      interline_virtual: 'virtual_warehouse.warehouse.types.interline_virtual',
    };

    const raw = (typeCode || fallbackName || 'default')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_');
    const key =
      map[raw] || `virtual_warehouse.warehouse.types.${raw || 'default'}`;
    const translated = this.translateService.instant(key);
    if (translated === key) {
      return fallbackName || typeCode || '';
    }
    return translated;
  }

  openWarehouseDialog(): void {
    const cityCodes = this.selectedCityCodes();
    if (cityCodes.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'virtual_warehouse.messages.error.title',
        ),
        detail: this.translateService.instant(
          'virtual_warehouse.form.placeholders.select_city_first',
        ),
        life: 4000,
      });
      return;
    }

    // seed initial selection from current selectedWarehouses
    this.dialogInitialSelection.set(
      this.mapToSelection(this.selectedWarehouses()),
    );
    this.showWarehouseDialog.set(true);
    this.loadDialogWarehouses(cityCodes);
  }

  private loadDialogWarehouses(cityCodes: string[]): void {
    this.dialogLoading.set(true);
    this.virtualWarehouseService
      .getPhysicalWarehouseCandidates(cityCodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          const candidates = list.map((c) => ({
            id: c.id,
            code: c.code || '',
            name: c.name || '',
            typeName: this.translateWarehouseType(
              (c as any).typeCode,
              (c as any).typeName,
            ),
            typeCode: (c as any).typeCode || '',
            contactName: (c as any).contactName || (c as any).linkman || '',
            contactPhone:
              (c as any).contactPhone ||
              (c as any).phone ||
              (c as any).mobile ||
              '',
            address: (c as any).address || (c as any).detailAddress || '',
          }));

          // Merge with current selections/cache to ensure dialog shows all
          const currentSelection = this.selectedWarehouses();
          const cachedMembers = this.warehouseData()?.memberWarehouses || [];
          const mergeSource = [
            ...candidates,
            ...currentSelection,
            ...cachedMembers.map((m: any) => ({
              ...m,
              typeName: this.translateWarehouseType(m.typeCode, m.typeName),
            })),
          ];
          const seen = new Set<number>();
          const merged = mergeSource.filter((w) => {
            if (!w?.id) return false;
            if (seen.has(w.id)) return false;
            seen.add(w.id);
            return true;
          });

          this.dialogBaseWarehouses.set(merged);
          this.dialogWarehouses.set(merged);
          this.dialogTotalRecords.set(merged.length);
          this.dialogLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.dialogLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  onDialogSelectionConfirm(selection: SelectionItem[]): void {
    const translatedSelection = selection.map((s: any) => ({
      ...s,
      typeName: this.translateWarehouseType(s.typeCode, s.typeName),
    }));
    this.selectedWarehouses.set(translatedSelection);
    this.setMemberWarehouseIds(translatedSelection.map((s) => s.id));
    this.refreshTableSections();
    this.cdr.markForCheck();
    this.showWarehouseDialog.set(false);
  }

  onDialogSearch(filter: {
    code?: string;
    name?: string;
    typeCode?: string;
  }): void {
    const base = this.dialogBaseWarehouses();
    const match = base.filter((w) => {
      const codeVal = (w as any).code || '';
      const nameVal = (w as any).name || '';
      const typeVal = (w as any).typeCode || (w as any).typeName || '';
      const codeOk = filter.code
        ? codeVal.toLowerCase().includes(filter.code.toLowerCase())
        : true;
      const nameOk = filter.name
        ? nameVal.toLowerCase().includes(filter.name.toLowerCase())
        : true;
      const typeOk = filter.typeCode
        ? typeVal.toLowerCase().includes(filter.typeCode.toLowerCase())
        : true;
      return codeOk && nameOk && typeOk;
    });
    this.dialogWarehouses.set(match);
    this.dialogTotalRecords.set(match.length);
  }

  onDialogSearchReset(): void {
    const base = this.dialogBaseWarehouses();
    this.dialogWarehouses.set(base);
    this.dialogTotalRecords.set(base.length);
  }
  private loadWarehouseData(id: number): Promise<VirtualWarehouse> {
    this.loading.set(true);
    return new Promise((resolve, reject) => {
      this.virtualWarehouseService
        .getVirtualWarehouseById(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (warehouse) => {
            // Store warehouse data to access members later
            this.selectedWarehouseData.set(warehouse);

            const formData: any = {
              ...warehouse,
              cityCode: null,
              memberWarehouseIds: warehouse.memberWarehouseIds || [],
            };

            const cityCodes: string[] = warehouse.memberWarehouseCityCodes
              ? Array.from(
                  new Set(
                    warehouse.memberWarehouseCityCodes.map((c) => c.toString()),
                  ),
                )
              : [];
            formData.cityCode = cityCodes;
            this.selectedCityCodes.set(cityCodes);
            this.loadPhysicalWarehouses(cityCodes, warehouse);

            this.setFormData(formData);
            resolve(warehouse);
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'virtual_warehouse.messages.error.title',
              ),
              detail: this.translateService.instant(
                'virtual_warehouse.messages.error.failed_to_load',
              ),
              life: 6000,
            });
            this.loading.set(false);
            reject(error);
          },
        });
    });
  }

  private setFormData(formData: any): void {
    this.updateFormFields();

    setTimeout(() => {
      const updatedFormData = { ...formData };
      this.warehouseData.set(updatedFormData as VirtualWarehouse);
      const memberWarehouses = (updatedFormData.memberWarehouses || []).map(
        (m: any) => ({
          ...m,
          typeName: this.translateWarehouseType(m.typeCode, m.typeName),
        }),
      );
      if (memberWarehouses.length) {
        this.candidateWarehouses.set(memberWarehouses);
        this.selectedWarehouses.set(memberWarehouses);
        this.setMemberWarehouseIds(
          memberWarehouses.map((m: any) => m.id as number),
        );
        this.refreshTableSections();
        this.cdr.markForCheck();
      }
      this.cdr.markForCheck();

      setTimeout(() => {
        const tryUpdateForm = (attempts: number = 0) => {
          if (this.addFormComponent) {
            const form = (this.addFormComponent as any).form;
            if (form) {
              const cityCodeControl = form.get('cityCode');
              const memberWarehouseIdsControl = form.get('memberWarehouseIds');

              if (
                cityCodeControl &&
                formData.cityCode !== null &&
                formData.cityCode !== undefined
              ) {
                cityCodeControl.setValue(formData.cityCode, {
                  emitEvent: false,
                });
              }

              if (memberWarehouseIdsControl) {
                const ids =
                  formData.memberWarehouseIds &&
                  Array.isArray(formData.memberWarehouseIds)
                    ? formData.memberWarehouseIds
                    : [];
                memberWarehouseIdsControl.setValue(ids, { emitEvent: false });
              }

              this.cdr.detectChanges();
              this.loading.set(false);
              return;
            }
          }

          if (attempts < 10) {
            setTimeout(() => tryUpdateForm(attempts + 1), 200);
          } else {
            this.warehouseData.set({ ...updatedFormData } as VirtualWarehouse);
            this.cdr.detectChanges();
            this.loading.set(false);
          }
        };

        tryUpdateForm();
      }, 500);
    }, 500);
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'cancel':
        this.onCancel();
        break;
      case 'save':
        this.onSubmit(event.data);
        break;
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    const memberSectionTitle = this.translateService.instant(
      'virtual_warehouse.form.sections.member_warehouses',
    );
    if (event.section !== memberSectionTitle) return;

    if (event.action === 'selectWarehouses') {
      this.openWarehouseDialog();
      return;
    }

    if (event.action === 'selectionChange') {
      const selection = event.data || [];
      this.selectedWarehouses.set(selection);
      this.setMemberWarehouseIds(selection.map((s: any) => s.id));
      this.refreshTableSections();
    }

    if (event.action === 'deleteSelected') {
      const selection = event.data || [];
      if (!selection.length) return;
      const selectedIds = new Set(selection.map((s: any) => s.id));
      const remaining = this.selectedWarehouses().filter(
        (w) => !selectedIds.has(w.id),
      );
      this.selectedWarehouses.set(remaining);
      this.setMemberWarehouseIds(remaining.map((w) => w.id));
      this.refreshTableSections();
    }
  }

  onFormValueChanges(formData: any): void {
    const currentCityCodes = Array.isArray(formData.cityCode)
      ? formData.cityCode
      : formData.cityCode
        ? [formData.cityCode]
        : [];
    const currentCityCodesStr = currentCityCodes.join(',');

    // Handle city change
    if (
      currentCityCodes.length > 0 &&
      currentCityCodesStr !== this.lastCityCode
    ) {
      this.lastCityCode = currentCityCodesStr;
      this.selectedCityCodes.set(currentCityCodes);
      this.loadPhysicalWarehouses(currentCityCodes);
      this.refreshTableSections();
    } else if (currentCityCodes.length === 0) {
      this.selectedCityCodes.set([]);
      this.physicalWarehouseOptions.set([]);
      this.candidateWarehouses.set([]);
      this.selectedWarehouses.set([]);
      this.refreshTableSections();
    }

    // Sync selection from form control or existing data to table when ids change
    const memberIds = Array.isArray(formData.memberWarehouseIds)
      ? formData.memberWarehouseIds
      : [];
    if (memberIds.length && this.candidateWarehouses().length > 0) {
      const selectionFromIds = this.candidateWarehouses().filter((c) =>
        memberIds.includes(c.id),
      );
      this.selectedWarehouses.set(selectionFromIds);
      this.refreshTableSections();
      this.cdr.markForCheck();
      return;
    }

    // Fallback to memberWarehouses from warehouseData (edit mode) if available
    const memberWarehouses = this.warehouseData()?.memberWarehouses || [];
    if (memberWarehouses.length && this.candidateWarehouses().length > 0) {
      this.selectedWarehouses.set(memberWarehouses);
      this.setMemberWarehouseIds(memberWarehouses.map((m) => m.id));
      this.refreshTableSections();
      this.cdr.markForCheck();
    }
  }

  private loadPhysicalWarehouses(
    cityCodes: string[],
    warehouseDetail?: VirtualWarehouse,
  ): void {
    // If edit mode already has member warehouses loaded, keep them and skip candidate API
    if (
      this.isEditMode() &&
      this.branchId &&
      this.loadedBranchMembers &&
      (this.warehouseData()?.memberWarehouses?.length ||
        this.branchDetailCache?.memberWarehouses?.length) &&
      this.candidateWarehouses().length
    ) {
      this.loadingPhysicalWarehouses.set(false);
      this.refreshTableSections();
      return;
    }

    if (!cityCodes || cityCodes.length === 0) {
      this.physicalWarehouseOptions.set([]);
      this.candidateWarehouses.set([]);
      this.selectedWarehouses.set([]);
      this.refreshTableSections();
      return;
    }

    this.loadingPhysicalWarehouses.set(true);
    this.refreshTableSections();

    const memberData =
      warehouseDetail?.memberWarehouses ||
      this.branchDetailCache?.memberWarehouses ||
      this.warehouseData()?.memberWarehouses ||
      [];

    const source$ =
      this.isEditMode() &&
      this.branchId &&
      !this.loadedBranchMembers &&
      memberData.length
        ? of(memberData)
        : this.virtualWarehouseService.getPhysicalWarehouseCandidates(
            cityCodes,
          );

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (candidates: any[]) => {
        // When loading from branch detail, mark as loaded to avoid refetch on subsequent city change
        if (this.isEditMode() && this.branchId) {
          this.loadedBranchMembers = true;
        }

        const options = candidates.map((c) => ({
          label: `${c.name} (${c.code})`,
          value: c.id,
        }));
        const candidateTable = candidates.map((c: any) => ({
          id: c.id,
          code: c.code,
          name: c.name,
          typeCode: c.typeCode,
          typeName: this.translateWarehouseType(c.typeCode, c.typeName),
          contactName: c.contactName,
          contactPhone: c.contactPhone,
          address: c.address,
        }));

        this.physicalWarehouseOptions.set(options);
        this.candidateWarehouses.set(candidateTable);
        this.loadingPhysicalWarehouses.set(false);
        this.refreshTableSections();

        // Sync selection based on current form control or fallback data
        setTimeout(() => {
          const form = (this.addFormComponent as any)?.form;
          const memberWarehouseIdsControl = form?.get('memberWarehouseIds');
          const controlIds: number[] | undefined =
            memberWarehouseIdsControl?.value;

          let selectionFromIds: Array<{
            id: number;
            code: string;
            name: string;
            typeCode?: string;
            typeName?: string;
            contactName?: string;
            contactPhone?: string;
            address?: string;
          }> = [];

          if (controlIds && Array.isArray(controlIds)) {
            selectionFromIds = candidateTable.filter((c) =>
              controlIds.includes(c.id),
            );
          }

          // Fallback from warehouseData.memberWarehouses if none matched
          if (
            selectionFromIds.length === 0 &&
            this.warehouseData()?.memberWarehouses?.length
          ) {
            selectionFromIds = this.warehouseData()?.memberWarehouses || [];
            if (memberWarehouseIdsControl) {
              memberWarehouseIdsControl.setValue(
                selectionFromIds.map((m) => m.id),
                {
                  emitEvent: false,
                },
              );
            }
          }

          this.selectedWarehouses.set(selectionFromIds);
          this.refreshTableSections();
          this.cdr.detectChanges();
        }, 100);

        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading physical warehouses:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'virtual_warehouse.messages.error.title',
          ),
          detail: this.translateService.instant(
            'virtual_warehouse.messages.error.failed_to_load_warehouses',
          ),
          life: 6000,
        });
        this.physicalWarehouseOptions.set([]);
        this.candidateWarehouses.set([]);
        this.selectedWarehouses.set([]);
        this.loadingPhysicalWarehouses.set(false);
        this.refreshTableSections();
      },
    });
  }

  private setMemberWarehouseIds(ids: number[]): void {
    const form = (this.addFormComponent as any)?.form;
    if (form) {
      const control = form.get('memberWarehouseIds');
      if (control) {
        control.setValue(ids, { emitEvent: false });
        this.cdr.markForCheck();
      }
    }
  }

  private refreshTableSections(): void {
    const current = this.formConfig();
    this.formConfig.set({
      ...current,
      tableSections: this.getTableSections(),
    });
  }

  onPhysicalWarehouseTableActionClick(actionType: 'view', row: any) {
    const warehouseId = row?.id;
    if (!warehouseId) return;

    switch (actionType) {
      case 'view': {
        this.onViewPhysicalWarehouse(warehouseId);
        return;
      }
    }
  }

  private onViewPhysicalWarehouse(warehouseId: number): void {
    const physicalWarehousePageURL = '/warehouse/logical/edit';
    if (this.isFormDirty()) {
      const urlTree = this.router.createUrlTree([
        physicalWarehousePageURL,
        warehouseId,
      ]);
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
      return;
    }
    this.router.navigate([physicalWarehousePageURL, warehouseId]);
  }

  private isFormDirty(): boolean {
    const form = this.addFormComponent?.form;
    return !!form?.dirty;
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.submitting()) {
      return true;
    }
    return !this.isFormDirty();
  }

  onSubmit(formData?: any): void {
    if (!formData) {
      this.submitting.set(false);
      return;
    }

    this.submitting.set(true);

    // Validate required fields
    if (
      !formData.name ||
      formData.status === undefined ||
      !formData.cityCode ||
      !formData.memberWarehouseIds ||
      !Array.isArray(formData.memberWarehouseIds) ||
      formData.memberWarehouseIds.length === 0
    ) {
      const memberIds = formData.memberWarehouseIds;
      const noWarehouseSelected =
        !memberIds || !Array.isArray(memberIds) || memberIds.length === 0;

      const phKey =
        'virtual_warehouse.messages.validation.select_physical_warehouse';
      const requiredKey =
        'virtual_warehouse.messages.validation.fill_required_fields';
      const rawDetail = noWarehouseSelected
        ? this.translateService.instant(phKey)
        : this.translateService.instant(requiredKey);

      const detail =
        rawDetail === phKey || rawDetail === requiredKey
          ? noWarehouseSelected
            ? this.translateService.currentLang === 'vi'
              ? 'Vui lòng chọn ít nhất một kho vật lý'
              : 'Please select at least one physical warehouse'
            : this.translateService.currentLang === 'vi'
              ? 'Vui lòng điền đầy đủ các trường bắt buộc'
              : 'Please fill in all required fields'
          : rawDetail;

      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'virtual_warehouse.messages.error.title',
        ),
        detail,
        life: 6000,
      });
      this.cdr.markForCheck();
      this.submitting.set(false);
      return;
    }

    // Get city name from selected province
    const provinceId = parseInt(formData.cityCode, 10);
    const province = this.provinces().find((p) => p.id === provinceId);
    if (!province) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'virtual_warehouse.messages.error.title',
        ),
        detail: this.translateService.instant(
          'virtual_warehouse.messages.error.invalid_city',
        ),
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    const cityName =
      this.translateService.currentLang === 'vi'
        ? province.full_name
        : province.full_name_en;

    if (this.isEditMode()) {
      const updateData: VirtualWarehouseUpdateRequest = {
        name: formData.name,
        status: formData.status !== undefined ? formData.status : true,
        memberWarehouseIds: formData.memberWarehouseIds,
      };

      this.virtualWarehouseService
        .updateVirtualWarehouse(this.warehouseData()!.id!, updateData)
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'virtual_warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'virtual_warehouse.messages.success.updated',
              ),
              life: 5000,
            });
            this.cdr.detectChanges();
            // Check if there's a returnUrl to navigate back to
            const returnUrl = this.route.snapshot.queryParams['returnUrl'];
            setTimeout(() => {
              if (returnUrl) {
                this.router.navigateByUrl(returnUrl);
              } else {
                this.router.navigate(['/warehouse/virtual']);
              }
            }, 2000);
          },
          error: (error: HttpErrorResponse) => {
            const fallbackKey =
              'virtual_warehouse.messages.error.failed_to_update';
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error?.message,
                fallbackKey,
              ),
            );
            this.submitting.set(false);
          },
        });
    } else {
      const warehouseData: VirtualWarehouseCreateRequest = {
        name: formData.name,
        status: formData.status !== undefined ? formData.status : true,
        memberWarehouseIds: formData.memberWarehouseIds,
      };

      this.virtualWarehouseService
        .createVirtualWarehouse(warehouseData)
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'virtual_warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'virtual_warehouse.messages.success.created',
              ),
              life: 5000,
            });
            this.cdr.detectChanges();
            // Check if there's a returnUrl to navigate back to
            const returnUrl = this.route.snapshot.queryParams['returnUrl'];
            setTimeout(() => {
              if (returnUrl) {
                this.router.navigateByUrl(returnUrl);
              } else {
                this.router.navigate(['/warehouse/virtual']);
              }
            }, 2000);
          },
          error: (error) => {
            const fallbackKey =
              'virtual_warehouse.messages.error.failed_to_create';
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error?.message,
                fallbackKey,
              ),
            );
            this.submitting.set(false);
          },
        });
    }
  }

  onCancel(): void {
    // Check if there's a returnUrl to navigate back to
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/warehouse/virtual']);
    }
  }

  onSectionAction(action: any): void {
    // Handle section actions if needed
  }
}
